import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMenu } from '@happyspace/sdk/restaurant';
import {
  ShieldCheck,
  Sparkles,
  HeartHandshake,
  Leaf,
  ChefHat,
  Users,
  ArrowRight,
  MapPin,
} from 'lucide-react';
import PageHero from '../components/PageHero';
import { HERO_IMAGE } from './Home';
import { SITE } from '../lib/site';

const VALUES = [
  {
    icon: ShieldCheck,
    title: 'Halal, No Exceptions',
    desc: 'Every cut of meat we serve is halal certified. It is the whole reason this kitchen exists, and we never compromise on it.',
  },
  {
    icon: Leaf,
    title: 'Fresh, Never Frozen Corners',
    desc: 'Vegetables prepped daily, meat marinated in house and bread warmed to order. Freshness is what you taste first.',
  },
  {
    icon: ChefHat,
    title: 'Authentic Recipes',
    desc: 'Shawarma spiced the traditional way, NYC-style gyro platters and Mediterranean sides made the way they should be.',
  },
  {
    icon: Sparkles,
    title: 'Clean Kitchen, Always',
    desc: 'Hygiene is not a checklist for us. Our prep areas are cleaned continuously through every service.',
  },
  {
    icon: HeartHandshake,
    title: 'Made With Care',
    desc: 'Every order is built by hand, one at a time. If it is not something we would serve our own family, it does not leave the counter.',
  },
  {
    icon: Users,
    title: 'Built For Our Neighbours',
    desc: 'We cook for local families, students and businesses around Plano, from a quick lunch wrap to a feast for four.',
  },
];

export default function About() {
  // Update page title and meta tags
  React.useEffect(() => {
    document.title = 'About Yalla Eat - 100% Halal Food Kitchen in Plano, TX';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Learn about Yalla Eat, Plano\'s dedicated 100% halal food kitchen. Fresh shawarma, authentic recipes, and community-focused service since day one.');
  }, []);

  const { items, categories } = useMenu();

  const stats = [
    { value: items.length ? `${items.length}` : 'N/A', label: 'Dishes on the menu' },
    { value: categories.length ? `${categories.length}` : 'N/A', label: 'Menu categories' },
    { value: '100%', label: 'Halal certified' },
    { value: `${SITE.pickupMinutes} min`, label: 'Typical pickup time' },
  ];

  return (
    <>
      <PageHero
        eyebrow="About Us"
        title="Good Food,"
        highlight="Good Mood"
        intro="Yalla Eat is a halal kitchen in Plano, Texas serving shawarma, NYC-style gyro platters, burgers, wings and family feasts, all prepared fresh to order."
      />

      {/* Story */}
      <section className="pt-6 md:pt-8 pb-12 md:pb-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.55 }}
          >
            <span
              className="text-xs font-extrabold uppercase tracking-[0.25em]"
              style={{ color: 'var(--color-ink)' }}
            >
              Our Story
            </span>
            <h2 className="mt-3 display text-3xl md:text-4xl text-ink rule-gold">
              A Halal Kitchen For Plano
            </h2>
            <div className="mt-6 space-y-4 text-[15px] text-ink-2 leading-relaxed">
              <p>
                Yalla Eat started with one simple idea: halal food should not mean settling. Not on
                flavour, not on freshness, and not on how quickly you can get a proper meal on a
                busy weekday.
              </p>
              <p>
                So we built a menu around the things people actually want to eat, lamb and beef
                shawarma carved to order, chicken gyro platters over seasoned rice, crispy zinger
                burgers, six styles of wings, and family combos big enough to feed four.
              </p>
              <p>
                Everything on our menu is <strong className="text-ink">100% halal</strong>. Our
                meat is certified, our kitchen is kept spotless, and every order is made when you
                order it, never sitting under a lamp waiting for someone to show up.
              </p>
              <p>
                Whether it is a quick lunch wrap between meetings or a mixed family shawarma
                platter for a get-together at home, we would love to feed you.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/menu" className="btn-green">
                See the Menu <ArrowRight size={15} />
              </Link>
              <Link to="/contact" className="btn-outline">
                <MapPin size={15} /> Find Us
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="relative"
          >
            <img
              src={HERO_IMAGE}
              alt="A freshly plated halal rice platter from Yalla Eat"
              className="w-full rounded-lg object-cover aspect-[4/3]"
            />
            <span
              className="halal-badge absolute -bottom-6 -left-4 md:-left-6 shadow-lg"
              style={{ width: 96, height: 96, background: '#fff' }}
            >
              <span className="font-bold text-xl" style={{ color: 'var(--color-green)' }}>حلال</span>
              <span className="text-[11px] font-extrabold tracking-widest text-ink">HALAL</span>
            </span>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section style={{ background: 'var(--color-ink)' }}>
        <div className="max-w-screen-xl mx-auto px-5 md:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="display text-4xl md:text-5xl" style={{ color: 'var(--color-yellow)' }}>
                {s.value}
              </p>
              <p className="mt-2 text-[12px] font-bold uppercase tracking-wide text-white/60">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-12 md:py-16" style={{ background: 'var(--color-cream)' }}>
        <div className="max-w-screen-xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span
              className="inline-flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.25em]"
              style={{ color: 'var(--color-ink)' }}
            >
              <span className="h-px w-8" style={{ background: 'var(--color-yellow)' }} />
              What We Stand For
              <span className="h-px w-8" style={{ background: 'var(--color-yellow)' }} />
            </span>
            <h2 className="mt-3 display text-3xl md:text-4xl text-ink">Our Promise To You</h2>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
                className="rounded-lg bg-white border p-6"
                style={{ borderColor: 'var(--color-line)' }}
              >
                <span
                  className="inline-flex w-12 h-12 rounded-full border-2 items-center justify-center"
                  style={{ borderColor: 'var(--color-yellow)' }}
                >
                  <v.icon size={20} style={{ color: 'var(--color-ink)' }} />
                </span>
                <h3
                  className="mt-4 text-[14px] font-extrabold uppercase tracking-wide"
                  style={{ color: 'var(--color-ink)' }}
                >
                  {v.title}
                </h3>
                <p className="mt-2 text-[13px] text-ink-2 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* JSON-LD: Organization details with mission */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `{\n        "@context": "https://schema.org",\n        "@type": "Organization",\n        "@id": "https://yallaeathalalfood.com/about",\n        "name": "Yalla Eat",\n        "description": "A dedicated 100% halal food kitchen in Plano, Texas serving fresh shawarma, gyro platters, burgers, wings and family combos.",\n        "url": "https://yallaeathalalfood.com/",\n        "founder": {\n          "@type": "Person",\n          "name": "Yalla Eat Team"\n        },\n        "location": {\n          "@type": "Place",\n          "name": "Plano, TX",\n          "address": {\n            "@type": "PostalAddress",\n            "streetAddress": "2380 E Park Blvd Ste 404",\n            "addressLocality": "Plano",\n            "addressRegion": "TX",\n            "postalCode": "75074",\n            "addressCountry": "US"\n          }\n        },\n        "sameAs": [\n          "https://www.instagram.com/yallaeathalal/",\n          "https://www.facebook.com/profile.php?id=61592194765349",\n          "https://www.tiktok.com/@yalla.eat4"\n        ],\n        "knowsAbout": ["Halal Food", "Shawarma", "Gyro", "Mediterranean Cuisine", "Middle Eastern Cuisine"],\n        "slogan": "100% Halal Food. No Exceptions."\n      }` }} />
    </>
  );
}
